import { useEffect, useMemo, useState } from "react";

import { extractBookmarkUrlFromText } from "src/spintr/components/Composer/utils";
import api from "src/spintr/SpintrApi";

export interface IMessageLinkCardMetadata {
    url: string;
    title?: string;
    description?: string;
    image?: string;
}

interface IResolvedMetadata {
    url: string;
    metadata: IMessageLinkCardMetadata;
}

/**
 * The social feed's metadata endpoint scrapes the target page on every call,
 * so answers are kept for the lifetime of the session. Chat history is
 * virtualized and the same link often shows up in several messages - without
 * this, every scrollback would trigger a new scrape. Failed requests are not
 * answers and are dropped again, see fetchMetadata.
 */
const requestCache = new Map<string, Promise<IMessageLinkCardMetadata>>();

/**
 * Answers that have already arrived, readable synchronously. The message list
 * is virtualized, so a message that scrolls out of view and back has to render
 * its card in its very first render: if the card only appeared once an effect
 * had resolved - even from a cached promise, which still takes a microtask -
 * the message would be measured without it and grow immediately afterwards,
 * and the list would jump every time a card scrolled back into view.
 *
 * A resolved null is an answer ("nothing to show") and belongs here. A failed
 * request is not, and stays out so it can be retried.
 */
const resolvedCache = new Map<string, IMessageLinkCardMetadata>();

/**
 * Maps a bookmark projection onto the metadata a card needs, showing whatever
 * the endpoint found and nothing more - the same as the social feed. Missing
 * fields are left out and Bookmark omits them, so a link the scraper can't
 * read still gets a card with its domain and url rather than no card at all.
 *
 * Returns null only when there is nothing to point the card at, which means
 * no card and the raw url stays visible.
 */
const mapBookmarkMetadata = (
    data: Spintr.IBookmarkProjection,
    requestedUrl: string,
): IMessageLinkCardMetadata => {
    if (!data) {
        return null;
    }

    const url = data.url || requestedUrl;
    if (!url) {
        return null;
    }

    return {
        url,
        title: data.title || undefined,
        description: data.description || undefined,
        image: data.images && data.images.length > 0 ? data.images[0] : undefined
    };
};

const fetchMetadata = (url: string): Promise<IMessageLinkCardMetadata> => {
    const cachedRequest = requestCache.get(url);
    if (cachedRequest) {
        return cachedRequest;
    }

    const request = api
        .get<Spintr.IBookmarkProjection>("/api/bookmarks/fetchimages", { params: { url } })
        .then((response) => {
            const metadata = mapBookmarkMetadata(response.data, url);

            resolvedCache.set(url, metadata);

            return metadata;
        })
        .catch(() => {
            // A request that never arrived says nothing about the link, so it
            // is dropped from the cache instead of being remembered as "no
            // card" for the rest of the session. The entry is set below before
            // this runs, so deleting it here is safe. A resolved null is a real
            // answer - the endpoint found nothing - and stays cached.
            requestCache.delete(url);

            return null;
        });

    requestCache.set(url, request);

    return request;
};

/**
 * Resolves link card metadata for a chat message text, reusing the social
 * feed's url detection and metadata endpoint. Null while the lookup is in
 * flight and when it failed, so no card is rendered in either case.
 */
const useMessageLinkCard = (text: string): IMessageLinkCardMetadata => {
    // A url string rather than the match object, so that the effect below only
    // re-runs when the url itself changes. The composer calls this on every
    // keystroke, and a fresh object each time would restart the lookup for a
    // url that hasn't changed.
    const url = useMemo(() => {
        const match = extractBookmarkUrlFromText(text);

        return match ? match.url : null;
    }, [text]);

    // Keyed by url so an edited message doesn't briefly show the previous
    // link's card while the new lookup is in flight.
    const [resolved, setResolved] = useState<IResolvedMetadata>(null);

    useEffect(() => {
        // An answer that is already in hand is returned during render below,
        // so there is nothing to wait for and no state to set.
        if (!url || resolvedCache.has(url)) {
            return;
        }

        let isCurrent = true;

        fetchMetadata(url).then((metadata) => {
            if (!isCurrent) {
                return;
            }

            setResolved({ url, metadata });
        });

        return () => {
            isCurrent = false;
        };
    }, [url]);

    if (!url) {
        return null;
    }

    if (resolvedCache.has(url)) {
        return resolvedCache.get(url);
    }

    return resolved?.url === url ? resolved.metadata : null;
};

export default useMessageLinkCard;
